import UmsUserManagementActionTypes from './types';

function formatPrivilege(privilege) {
  const formattedPrivleges = [];
  const crud = ['read', 'create', 'update', 'delete'];
  if (privilege.length !== 0) {
    privilege.forEach((pr) => {
      const currentPrivilege = [];
      const isWorkflowModule = pr.name && (
        pr.name.toLowerCase().includes('workflow') || 
        pr.name.toLowerCase().includes('safapply workflow')
      );
      
      crud.forEach((crudOp) => {
        let found = false;
        pr.privileges.forEach((eachPrivilege) => {
          if (crudOp === eachPrivilege.description.toLowerCase()) {
            found = true;
            // Only disable if the privilege has no name (empty or missing)
            // If it has a name, it should be enabled regardless of any existing disabled flag
            const hasValidName = eachPrivilege.name && eachPrivilege.name.trim() !== '';
            
            // For workflow module, if privilege doesn't have a name, try to construct it
            // This handles cases where backend returns workflow privileges without names
            if (isWorkflowModule && !hasValidName) {
              const workflowName = `workflow:${crudOp === 'read' ? 'r' : crudOp === 'create' ? 'c' : crudOp === 'update' ? 'u' : 'd'}`;
              currentPrivilege.push({
                ...eachPrivilege,
                name: workflowName,
                description: eachPrivilege.description || (crudOp[0].toUpperCase() + crudOp.substr(1)),
                disabled: false, // Enable workflow privileges even if they don't have names from backend
              });
            } else {
              currentPrivilege.push({
                ...eachPrivilege,
                disabled: !hasValidName,
              });
            }
          }
        });
        if (!found) {
          // For workflow module, create enabled placeholder with proper name
          if (isWorkflowModule) {
            const workflowName = `workflow:${crudOp === 'read' ? 'r' : crudOp === 'create' ? 'c' : crudOp === 'update' ? 'u' : 'd'}`;
            currentPrivilege.push({
              description: crudOp[0].toUpperCase() + crudOp.substr(1),
              name: workflowName,
              disabled: false, // Enable workflow privileges
            });
          } else {
            currentPrivilege.push({
              description: crudOp[0].toUpperCase() + crudOp.substr(1),
              name: '',
              disabled: true,
            });
          }
        }
      });
      formattedPrivleges.push({
        ...pr,
        privileges: currentPrivilege,
      });
    });
  }
  return formattedPrivleges;
}

const initialState = {
  users: {
    data: []
  },
  usersLoading: false,
  roles: [],
  rolesLoading: false,
  currentRole: null,
  currentRoleLoading: false,
  savingRole: false,
  deletingRole: false,
  privileges: [],
  privilegesLoading: false,
  statistics: null,
  statisticsLoading: false,
  error: null,
};

function UmsUserManagementReducer(state = initialState, { type, payload }) {
  switch (type) {
    case UmsUserManagementActionTypes.SET_ANY_DATA:
      return {
        ...state,
        ...payload,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_USERS_START:
      return {
        ...state,
        usersLoading: true,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_USERS_SUCCESS:
      return {
        ...state,
        usersLoading: false,
        users: payload,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_USERS_FAILURE:
      return {
        ...state,
        usersLoading: false,
        error: payload,
      };

    case UmsUserManagementActionTypes.DELETE_ROLE_START:
      return {
        ...state,
        deletingRole: true,
      };
    case UmsUserManagementActionTypes.DELETE_ROLE_SUCCESS:
      return {
        ...state,
        deletingRole: false,
        roles: state.roles.filter((role) => role.id !== payload),
      };
    case UmsUserManagementActionTypes.DELETE_ROLE_FAILURE:
      return {
        ...state,
        deletingRole: false,
      };
    case UmsUserManagementActionTypes.SAVE_ROLE_START:
      return {
        ...state,
        savingRole: true,
      };
    case UmsUserManagementActionTypes.EDIT_ROLE_START:
      return {
        ...state,
        savingRole: true,
      };
    case UmsUserManagementActionTypes.SAVE_ROLE_SUCCESS:
      return {
        ...state,
        savingRole: false,
        roles: [payload, ...state.roles],
      };
    case UmsUserManagementActionTypes.EDIT_ROLE_SUCCESS:
      return {
        ...state,
        savingRole: false,
        roles: state.roles.map((role) =>
          role.id === payload.id ? { ...payload } : role
        ),
      };
    case UmsUserManagementActionTypes.SAVE_ROLE_FAILURE:
      return {
        ...state,
        savingRole: false,
        error: payload,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_ROLES_START:
      return {
        ...state,
        rolesLoading: true,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_ROLES_SUCCESS:
      return {
        ...state,
        rolesLoading: false,
        roles: payload,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_ROLES_FAILURE:
      return {
        ...state,
        rolesLoading: false,
        error: payload,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_SINGLE_ROLE_START:
      return {
        ...state,
        currentRoleLoading: true,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_SINGLE_ROLE_SUCCESS:
      return {
        ...state,
        currentRoleLoading: false,
        currentRole: payload,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_SINGLE_ROLE_FAILURE:
      return {
        ...state,
        currentRoleLoading: false,
        error: payload,
      };

    case UmsUserManagementActionTypes.FETCH_UMS_PRIVILEGES_START:
      return {
        ...state,
        rolesLoading: true,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_PRIVILEGES_SUCCESS:
      const formattedPrivileges = formatPrivilege(payload);
      return {
        ...state,
        rolesLoading: false,
        privileges: formattedPrivileges,
      };
    case UmsUserManagementActionTypes.FETCH_UMS_PRIVILEGES_FAILURE:
      return {
        ...state,
        rolesLoading: false,
        error: payload,
      };

    case UmsUserManagementActionTypes.FETCH_USER_STATISTICS_START:
      return {
        ...state,
        statisticsLoading: true,
      };
    case UmsUserManagementActionTypes.FETCH_USER_STATISTICS_SUCCESS:
      return {
        ...state,
        statisticsLoading: false,
        statistics: payload,
      };
    case UmsUserManagementActionTypes.FETCH_USER_STATISTICS_FAILURE:
      return {
        ...state,
        statisticsLoading: false,
        error: payload,
      };

    default:
      return state;
  }
}

export default UmsUserManagementReducer;
